/**
 * CustomerSearch component — modal overlay for searching and selecting a customer.
 *
 * Displays a full-screen modal with a search input, results list, a "Guest
 * Checkout" option, and an inline "Create New Customer" form. Debounces the
 * search input by 300 ms and handles stale-response discarding via a request
 * counter ref.
 *
 * @param {Object}   props
 * @param {Function} props.onSelect       - Called with the selected customer object, or null for guest checkout.
 * @param {Function} props.onClose        - Called when the modal should close (overlay click, Escape key, or close button).
 * @param {string}   [props.initialSearch] - Optional initial value for the search input.
 */
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createElement,
} from 'react';
import api from '../../api/client';
import CustomerForm from '../CustomerForm/index.jsx';

export default function CustomerSearch({ onSelect, onClose, initialSearch = '', crmEnabled = false }) {
  // ----- State ---------------------------------------------------------------
  const [search, setSearch] = useState(initialSearch);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('woo'); // 'woo' or 'crm'

  // Refs for debounce and stale-response tracking.
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const inputRef = useRef(null);

  // ----- Search execution ----------------------------------------------------

  /**
   * Execute the customer search API call.
   * Discards responses that arrive after a newer request has been sent.
   */
  const executeSearch = useCallback(async (term, tab) => {
    const currentId = ++requestIdRef.current;

    if (!term || !term.trim()) {
      setCustomers([]);
      setLoading(false);
      setHasSearched(false);
      return;
    }

    setLoading(true);

    try {
      let list;
      if (tab === 'crm') {
        const response = await api.searchCrmContacts(term.trim());
        if (currentId !== requestIdRef.current) return;
        list = response.contacts || [];
        // Normalize CRM contacts to match WC customer shape.
        list = list.map((c) => ({
          id: c.id,
          name: c.contactName || c.companyName || c.email,
          email: c.email || '',
          phone: c.phone || '',
          _source: 'crm',
        }));
      } else {
        const response = await api.getCustomers({ search: term.trim() });
        if (currentId !== requestIdRef.current) return;
        list = Array.isArray(response)
          ? response
          : response.customers ?? response.data ?? [];
      }

      setCustomers(list);
      setHasSearched(true);
    } catch {
      if (currentId !== requestIdRef.current) return;
      setCustomers([]);
      setHasSearched(true);
    } finally {
      if (currentId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // ----- Debounced effect on search change -----------------------------------

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      executeSearch(search, activeTab);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, activeTab, executeSearch]);

  // ----- Cleanup on unmount --------------------------------------------------

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ----- Focus the search input on mount -------------------------------------

  useEffect(() => {
    // Short timeout lets the modal animation start before the keyboard opens.
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // ----- Keyboard: close on Escape -------------------------------------------

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ----- Handlers ------------------------------------------------------------

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setShowForm(false);
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setCustomers([]);
    setHasSearched(false);
    setShowForm(false);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setShowForm(false);
    inputRef.current?.focus();
  }, []);

  const handleSelectGuest = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const handleSelectCustomer = useCallback(
    (customer) => {
      onSelect(customer);
    },
    [onSelect],
  );

  const handleCustomerCreated = useCallback(
    (customer) => {
      onSelect(customer);
    },
    [onSelect],
  );

  const handleOverlayClick = useCallback(
    (e) => {
      // Only close when clicking the backdrop itself, not the panel.
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  // ----- Render --------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[10vh]"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Customer search"
    >
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl flex flex-col max-h-[80vh] overflow-hidden">
        {/* ------- Header / Search input ------- */}
        <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">Select Customer</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1"
              aria-label="Close customer search"
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {crmEnabled && (
            <div className="flex gap-1 mb-3 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => handleTabChange('woo')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'woo'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                WooCommerce
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('crm')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'crm'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                CRM
              </button>
            </div>
          )}

          <div className="relative">
            {/* Magnifying glass icon */}
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by name, email, or phone..."
              className={
                'block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 ' +
                'text-sm text-gray-900 placeholder-gray-400 ' +
                'outline-none transition-shadow ' +
                'focus:border-pos-primary focus:ring-2 focus:ring-pos-primary/20'
              }
              aria-label="Search customers"
            />

            {/* Clear button */}
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ------- Results area (scrollable) ------- */}
        <div className="flex-1 overflow-y-auto">
          {/* Guest checkout — always visible */}
          <button
            type="button"
            onClick={handleSelectGuest}
            className={
              'w-full flex items-center gap-3 px-4 py-3 text-left ' +
              'hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100'
            }
          >
            {/* User-slash / guest icon */}
            <span className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-full bg-gray-100 text-gray-500">
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="17" y1="8" x2="23" y2="8" />
              </svg>
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Guest Checkout</p>
              <p className="text-xs text-gray-500">Continue without a customer account</p>
            </div>
          </button>

          {/* Loading spinner */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <svg
                className="h-6 w-6 animate-spin text-pos-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="ml-2 text-sm text-gray-500">Searching...</span>
            </div>
          )}

          {/* Customer results */}
          {!loading && customers.length > 0 && (
            <ul className="divide-y divide-gray-100" role="listbox" aria-label="Customer results">
              {customers.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected="false"
                    onClick={() => handleSelectCustomer(customer)}
                    className={
                      'w-full flex items-center gap-3 px-4 py-3 text-left ' +
                      'hover:bg-pos-primary/5 active:bg-pos-primary/10 transition-colors'
                    }
                  >
                    {/* Avatar circle with initials */}
                    <span className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-full bg-pos-primary/10 text-pos-primary text-sm font-semibold uppercase">
                      {(customer.first_name?.[0] || customer.name?.[0] || customer.email?.[0] || '?')}
                    </span>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {customer.name ||
                          [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
                          customer.email}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {customer.email && (
                          <span className="text-xs text-gray-500 truncate">{customer.email}</span>
                        )}
                        {customer.phone && (
                          <span className="text-xs text-gray-400 truncate">{customer.phone}</span>
                        )}
                      </div>
                    </div>

                    {/* Chevron right */}
                    <svg
                      className="h-4 w-4 flex-shrink-0 text-gray-300"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Empty state */}
          {!loading && hasSearched && customers.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <svg
                className="h-10 w-10 mb-2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
              <p className="text-sm font-medium">No customers found</p>
              <p className="text-xs mt-0.5">Try a different search or create a new customer</p>
            </div>
          )}

          {/* Create New Customer — inline form or toggle button */}
          {!showForm ? (
            <div className="px-4 py-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className={
                  'w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed ' +
                  'border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 ' +
                  'hover:border-pos-primary hover:text-pos-primary transition-colors'
                }
              >
                {/* Plus icon */}
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create New Customer
              </button>
            </div>
          ) : (
            <div className="border-t border-gray-200 bg-gray-50">
              <CustomerForm
                onCreated={handleCustomerCreated}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
